import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { Button, IconButton } from "react-native-paper";

interface ReportUserModalProps {
  visible: boolean;
  flaggedUserName: string;
  onClose: () => void;
  onSubmit: (reason: string, details: string) => Promise<void>;
  isSubmitting: boolean;
}

const PREDEFINED_REASONS = [
  "Inappropriate behavior",
  "Harassment or abuse",
  "Spam or scamming",
  "Other",
];

export default function ReportUserModal({
  visible,
  flaggedUserName,
  onClose,
  onSubmit,
  isSubmitting,
}: ReportUserModalProps) {
  const [selectedReason, setSelectedReason] = useState(PREDEFINED_REASONS[0]);
  const [details, setDetails] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!visible) return null;

  const handleSub = async () => {
    if (!details.trim()) {
      setError("Please describe what happened.");
      return;
    }
    setError(null);
    try {
      await onSubmit(selectedReason, details.trim());
      setDetails("");
      setSelectedReason(PREDEFINED_REASONS[0]);
    } catch {
      setError("Failed to submit report. Please try again.");
    }
  };

  return (
    <View style={styles.root}>
      <Pressable style={styles.backdrop} onPress={onClose} />

      <View style={styles.centered}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Report {flaggedUserName}</Text>
            <IconButton
              icon="close"
              size={20}
              onPress={onClose}
              disabled={isSubmitting}
              accessibilityLabel="Close"
            />
          </View>

          <View style={styles.content}>
            <Text style={styles.label}>SELECT REASON</Text>
            <View style={styles.reasonsContainer}>
              {PREDEFINED_REASONS.map((reason) => {
                const isSelected = selectedReason === reason;
                return (
                  <Pressable
                    key={reason}
                    style={[
                      styles.reasonOption,
                      isSelected && styles.reasonOptionSelected,
                    ]}
                    onPress={() => setSelectedReason(reason)}
                    disabled={isSubmitting}
                  >
                    <Text
                      style={[
                        styles.reasonText,
                        isSelected && styles.reasonTextSelected,
                      ]}
                    >
                      {reason}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.label}>WHAT HAPPENED? (REQUIRED)</Text>
            <TextInput
              value={details}
              onChangeText={(text) => {
                setDetails(text);
                if (error) setError(null);
              }}
              placeholder="Describe the behavior..."
              placeholderTextColor="#888"
              multiline
              numberOfLines={4}
              style={styles.textInput}
              disabled={isSubmitting}
            />

            {!!error && <Text style={styles.errorText}>{error}</Text>}
          </View>

          <View style={styles.actions}>
            <Button
              mode="outlined"
              onPress={onClose}
              disabled={isSubmitting}
              style={[styles.button, styles.cancelButton]}
              textColor="#333"
            >
              Cancel
            </Button>
            <Button
              mode="contained"
              onPress={handleSub}
              disabled={isSubmitting}
              buttonColor="#ff4444"
              textColor="#fff"
              style={styles.button}
            >
              {isSubmitting ? <ActivityIndicator color="#fff" /> : "Submit"}
            </Button>
          </View>
        </View>
      </View>
    </View>
  );
}

const { height: screenHeight } = Dimensions.get("window");

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "transparent",
    zIndex: 9999,
  },
  backdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 16,
    padding: 20,
    width: "100%",
    maxWidth: 480,
    elevation: 6,
    maxHeight: screenHeight * 0.85,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    color: "#ff4444",
  },
  content: {
    marginBottom: 24,
  },
  label: {
    fontSize: 12,
    fontWeight: "700",
    color: "#666",
    marginBottom: 10,
    letterSpacing: 0.5,
  },
  reasonsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 20,
  },
  reasonOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  reasonOptionSelected: {
    backgroundColor: "#fff0f0",
    borderColor: "#ff4444",
  },
  reasonText: {
    fontSize: 13,
    color: "#555",
  },
  reasonTextSelected: {
    color: "#ff4444",
    fontWeight: "600",
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fafafa",
    minHeight: 100,
    textAlignVertical: "top",
  },
  errorText: {
    color: "#ff4444",
    fontSize: 13,
    marginTop: 8,
    fontWeight: "500",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  button: {
    borderRadius: 10,
    minWidth: 100,
  },
  cancelButton: {
    borderColor: "#ccc",
  },
});
